import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x, nothing as A } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';
import { TextAlign } from '../pvd-utils/enums/text-align.js';

var InputType;
(function (InputType) {
    InputType["email"] = "email";
    InputType["number"] = "number";
    InputType["password"] = "password";
    InputType["phone"] = "phone";
    InputType["tel"] = "tel";
    InputType["text"] = "text";
    InputType["url"] = "url";
    InputType["ssn"] = "ssn";
    InputType["zip"] = "zip";
})(InputType || (InputType = {}));
const DEFAULT_SIZE = StandardSize.medium;
const DEFAULT_TYPE = InputType.text;
const DEFAULT_TEXT_ALIGN = TextAlign.left;
const DEFAULT_REQUIRED = false;
const DEFAULT_ERROR = false;
const DEFAULT_DISABLED = false;
const INPUT_PROTECTED_ATTR_MAP = new Map([
    ['aria-describedby', 'pvd-aria-describedby'],
    ['aria-disabled', 'pvd-disabled'],
    ['aria-invalid', 'pvd-error'],
    ['aria-label', 'pvd-aria-label'],
    ['aria-required', 'pvd-required'],
    ['disabled', 'pvd-disabled'],
    ['id', 'pvd-id'],
    ['pattern', 'pvd-pattern'],
    ['placeholder', 'pvd-placeholder'],
    ['required', 'pvd-required'],
    ['type', 'pvd-type'],
    ['value', 'pvd-value'],
]);
/**
 * Providence Input component.
 * @see {@link https://providence.fmr.com/4.x/components/input/input.html|Input documentation}
 */
class PvdInput extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        this.pvdDisabled = DEFAULT_DISABLED;
        this.pvdError = DEFAULT_ERROR;
        this.pvdRequired = DEFAULT_REQUIRED;
        this.pvdSize = DEFAULT_SIZE;
        this.pvdTextAlign = DEFAULT_TEXT_ALIGN;
        this.pvdType = DEFAULT_TYPE;
        // #endregion
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region private methods
    get hasIconLeft() {
        return !!this.pvdIconLeft || this.isSlotUsed('icon-left');
    }
    get hasIconRight() {
        return !!this.pvdIconRight || this.isSlotUsed('icon-right');
    }
    get iconSize() {
        return this.pvdSize === StandardSize.large ? Size.medium : Size.small;
    }
    get inputType() {
        switch (this.pvdType) {
            case InputType.phone:
                return InputType.tel;
            case InputType.ssn:
            case InputType.zip:
                return InputType.text;
            default:
                return this.pvdType;
        }
    }
    /**
     * Sets attributes on the input element that are coming from pvdNativeInputAttributes
     * @param prevAttr optional, if user updates pvdNativeInputAttributes, this will remove/update existing attributes
     */
    setNativeInputAttributes(prevAttr) {
        const targetElement = this.querySelector('input');
        this.setNativeAttributes(this.pvdNativeInputAttributes, INPUT_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    }
    /**
     * Callback to detect changes to the slotted content
     * @param event
     */
    onSlotChangeHandler(event) {
        this.requestUpdate();
    }
    // #endregion
    // #region events
    /**
     * Update the `pvdValue` property to match the new value of the <input> element
     * @param e
     */
    onInput(e) {
        this.pvdValue = e.target.value;
    }
    // #endregion
    // #region LitElement lifecycles
    updated(changedProperties) {
        super.updated(changedProperties);
        if (this.pvdNativeInputAttributes) {
            const prevAttr = changedProperties.get('pvdNativeInputAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeInputAttributes(prevAttr);
        }
    }
    renderLeftIcon() {
        return this.hasIconLeft
            ? x `<span class="pvd-input__icon pvd-input__icon--left"><s-slot name="icon-left" @slotchange="${this.onSlotChangeHandler}">${this.renderIcon(this.pvdIconLeft)}</s-slot></span>`
            : x `<s-slot name="icon-left" @slotchange="${this.onSlotChangeHandler}"></s-slot>`;
    }
    renderRightIcon() {
        return this.hasIconRight
            ? x `<span class="pvd-input__icon pvd-input__icon--right"><s-slot name="icon-right" @slotchange="${this.onSlotChangeHandler}">${this.renderIcon(this.pvdIconRight)}</s-slot></span>`
            : x `<s-slot name="icon-right" @slotchange="${this.onSlotChangeHandler}"></s-slot>`;
    }
    renderIcon(iconName) {
        if (!iconName) {
            return A;
        }
        return this.scopeTemplate `<pvd-scoped-icon
      pvd-name="${iconName}"
      pvd-size="${this.iconSize}"
    ></pvd-scoped-icon>`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-input-root`,
            modifierPrefix: 'pvd-input',
            modifiers: [
                {
                    condition: this.hasIconLeft,
                    className: 'icon-left',
                },
                {
                    condition: this.hasIconRight,
                    className: 'icon-right',
                },
                {
                    condition: this.pvdDisabled,
                    className: 'disabled',
                },
                {
                    condition: this.pvdError,
                    className: 'error',
                },
                {
                    condition: this.pvdFullWidth !== undefined,
                    className: `${this.pvdFullWidth ? 'full' : 'fixed'}-width`,
                },
                {
                    condition: [InputType.phone, InputType.ssn, InputType.zip].indexOf(this.pvdType) !== -1,
                    className: this.pvdType,
                },
                {
                    condition: true,
                    className: `${this.pvdSize}`,
                },
                {
                    condition: this.pvdTextAlign !== undefined,
                    className: `text-${this.pvdTextAlign}`,
                },
            ],
        };
        return x `<div class="${getClassName(classMap)}">${this.renderLeftIcon()} <input @input="${this.onInput}" @blur="${this.bubbleEvent}" @focus="${this.bubbleEvent}" ?disabled="${this.pvdDisabled}" ?required="${this.pvdRequired}" .value="${l(this.pvdValue)}" aria-describedby="${l(this.pvdAriaDescribedby)}" aria-disabled="${l(this.a11yBoolean(this.pvdDisabled))}" aria-invalid="${l(this.a11yBoolean(this.pvdError))}" aria-label="${l(this.pvdAriaLabel)}" aria-required="${l(this.a11yBoolean(this.pvdRequired))}" class="pvd-input__input" id="${l(this.pvdId)}" pattern="${l(this.pvdPattern)}" placeholder="${l(this.pvdPlaceholder)}" type="${this.inputType}"> ${this.renderRightIcon()}</div>`;
    }
}
//#region Component configuration
PvdInput.componentName = 'input';
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-describedby' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-aria-label' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdAriaLabel", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disabled',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disabled'),
    }),
    __metadata("design:type", Boolean)
], PvdInput.prototype, "pvdDisabled", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-error',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-error'),
    }),
    __metadata("design:type", Boolean)
], PvdInput.prototype, "pvdError", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-full-width',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-full-width'),
    }),
    __metadata("design:type", Boolean)
], PvdInput.prototype, "pvdFullWidth", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-icon-left' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdIconLeft", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-icon-right' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdIconRight", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-id' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdId", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-input-attributes' }),
    __metadata("design:type", Object)
], PvdInput.prototype, "pvdNativeInputAttributes", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-pattern' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdPattern", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-placeholder' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdPlaceholder", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-required',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-required'),
    }),
    __metadata("design:type", Boolean)
], PvdInput.prototype, "pvdRequired", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-size',
        ...PvdSlotifyBaseComponent.hasChanged(StandardSize),
    }),
    __metadata("design:type", Number)
], PvdInput.prototype, "pvdSize", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-text-align',
        ...PvdSlotifyBaseComponent.hasChanged(TextAlign),
    }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdTextAlign", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-type',
        ...PvdSlotifyBaseComponent.hasChanged(InputType),
    }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdType", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-value' }),
    __metadata("design:type", String)
], PvdInput.prototype, "pvdValue", void 0);

export { DEFAULT_DISABLED, DEFAULT_ERROR, DEFAULT_REQUIRED, DEFAULT_SIZE, DEFAULT_TEXT_ALIGN, DEFAULT_TYPE, INPUT_PROTECTED_ATTR_MAP, InputType, PvdInput };
//# sourceMappingURL=pvd-input.js.map
